import React, {useEffect, useState} from 'react';
import Layout from '@gakki/components/Layout';
import PageToolBar, {ToolDivider} from '@gakki/components/PageToolBar';
import config from '@gakki/step-config';
import usePageData from '@common/hooks/usePageData';
import useCrossTabState from '@common/hooks/use-cross-tab-state';
import {
  FailDialog,
  ResetDialog,
  ResetSuccessDialog,
} from 'app/gakki/components/Dialog';
import {useForm} from 'react-hook-form';
import cx from 'classnames';
import {StepStatus, useAppContext} from '@gakki/context/app-context';
import {useAuthContext, useClient} from '@gakki/context/auth-context';
import {useCompanyContext} from '@gakki/context/company-context';
import EmployeeSidebar from './components/employee-sidebar';
import FormContent from './components/form-content';
import SalarySubjectForm from './components/salary-subject-form';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IconButton} from 'components/Button';
import qs from 'qs';
import {useMutation} from 'react-query';
import {debounce, cloneDeep} from 'lodash';
import {toast} from 'react-toastify';
import {validate, isAfterDate} from './components/validate';
import useSalarySettingOptions from './hooks/useSalarySettingOptions';
import {useSaveSalaryAccounts} from '@gakki/api/payroll';
import {DevTool} from '@hookform/devtools';
import {
  SalaryAccount,
  SalaryCategory,
  SubjectCategory,
  DefaultBasicSubjectCode,
  defaultBasicSubjectCodes,
} from '@gakki/api/types';
import {subjectDataName} from './components/salary-subject-data';
import {useXlsExport} from 'hooks/useXlsExport';
import {usePost} from 'hooks/usePost';
import {ReactComponent as DownloadIcon} from 'app/gakki/assets/download.svg';
import {ReactComponent as UploadIcon} from 'app/gakki/assets/upload.svg';
const initData = {
  personalPayroll: {
    enabled: true,
    effectiveDate: null,
    bankId: null,
    bankBranchCode: '',
    salaryAccount: '',
    salaryCategory: 1,
  },
  laborInsurance: {
    enabled: true,
    effectiveDate: null,
    insuranceApplicantUnitId: null,
    nationality: 0,
    insuranceIdentityId: null,
    insuranceAmountLevelId: null,
    isDeduction: false,
    allowanceRate: '0',
    allowanceRemark: '',
  },
  occupationalInjuryInsurance: {
    enabled: true,
    effectiveDate: null,
    insuranceApplicantUnitId: null,
    nationality: 0,
    insuranceIdentityId: null,
    insuranceAmountLevelId: null,
    isDeduction: false,
    allowanceRate: '0',
    allowanceRemark: '',
  },
  retirement: {
    enabled: true,
    effectiveDate: null,
    category: 1,
    nationality: 0,
    insuranceApplicantUnitId: null,
    insuranceAmountLevelId: null,
    employerBurdenRate: '6',
    personalBurdenRate: '0',
  },
  healthInsurance: {
    enabled: true,
    effectiveDate: null,
    insuranceApplicantUnitId: null,
    insuranceAmountLevelId: null,
    nationality: 0,
    insuranceIdentity: '1',
    isDeduction: false,
    deductionRateType: 0,
    allowanceRate: '0',
    highestAllowanceAmount: '0',
    allowanceRemark: '',
  },
  dependentsHealthInsurance: {
    enabled: true,
    dependentsHealthInsuranceItems: [
      {
        nationality: 0,
        name: '',
        identityNo: '',
        birthday: null,
        nHIDependentsAddTitleCode: null,
        isChargeType: true,
        isDeductionType: false,
        deductionRateType: 0,
        allowanceRate: '0',
        highestAllowanceAmount: '0',
        allowanceRemark: '',
        addInsuranceDate: null,
      },
    ],
  },
  tax: {
    enabled: true,
    effectiveDate: null,
    incomeTaxReturnUnitId: null,
    incomeTaxNumberCode: null,
    withHoldingTypeCode: null,
    supplementaryPremiumPartTime: 0,
    supplementaryPremiumPartTimeUnitId: null,
    disadvantagedGroup: 0,
  },
  dependentsTax: {
    enabled: true,
    dependentsTaxItems: [
      {
        name: '',
        dependentsTitle: '',
        birthday: null,
        identityNo: '',
        address: '',
        dependentsExemptionConditionCode: null,
      },
    ],
  },
  payrollSubject: {
    payrollSubjectItems: [],
  },
};
export default function EmployeeSalarySetting() {
  const api = useClient();
  const {companyId} = useAuthContext();
  const {data, status, remove, init, edit, updatedTime} = usePageData<any>(
    'employee-salary-setting',
    [],
  );
  const {employee} = useCompanyContext();
  const {
    changeStep,
    step,
    setIsLoading,
    isLoading,
    save,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();
  const [currentEmployee, setCurrentEmployee] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [showSalarySubjectForm, setShowSalarySubjectForm] = useCrossTabState(
    'showSalarySubjectForm',
    false,
  );
  const [showFailDialog, setShowFailDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showUploadFailDialog, setShowUploadFailDialog] = useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] = useState(false);
  const [isInit, setIsInit] = useState(false);
  const [allErrors, setAllErrors] = useState<any>([]);
  const options = useSalarySettingOptions();
  const basicSalaryCategoryId = options.accountCategory.find(
    (c: any) => c.code === SubjectCategory.basicSalary,
  )?.id;
  const baseSalarySubject: SalaryAccount[] = options.salaryAccounts.filter(
    (s: SalaryAccount) =>
      !defaultBasicSubjectCodes.includes(s.hnSalaryAccountCode || '') &&
      s.salaryAccountCategoryId === basicSalaryCategoryId,
  );
  const {
    mutateAsync: saveSalaryAccount,
    isLoading: isSaveSalaryAccountLoading,
  } = useSaveSalaryAccounts();
  const {setData: exportExcelData} = useXlsExport();
  const {mutate} = usePost(`payroll/import/excel/${companyId}`, {
    onSuccess: async (data: any) => {
      if (data.success) {
        init(null);
        await options.refetchSalaryAccounts();
        setIsInit(false);
        toast.success('已上傳成功', {
          hideProgressBar: true,
          position: 'top-center',
        });
      } else {
        setErrorMessage(data.message);
        setShowUploadFailDialog(true);
      }
    },
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });
  const checkBankExist = (id: string) =>
    options.banks.some((b: any) => b.bankId === id) ? id : null;
  const checkInsuranceUnitExist = (id: string) =>
    options.insuranceUnits.some((b: any) => b.insuranceApplicantUnitId === id)
      ? id
      : null;
  const checkIncomeTaxUnitExist = (id: string) =>
    options.incomeTaxRegistrationUnits.some(
      (b: any) => b.incomeTaxReturnUnitId === id,
    )
      ? id
      : null;
  const formProps = useForm();

  const initEmployee = (defaultDatas: any = []) => {
    return {
      employeePayrollItems: employee.map((e: any, i: number) => {
        const defaultData = defaultDatas.find(
          (d: any) => d.employee.jobNo === e.jobNo,
        );
        if (defaultData) {
          defaultData.personalPayroll.bankId = checkBankExist(
            defaultData.personalPayroll.bankId,
          );
          defaultData.laborInsurance.insuranceApplicantUnitId =
            checkInsuranceUnitExist(
              defaultData.laborInsurance.insuranceApplicantUnitId,
            );
          defaultData.occupationalInjuryInsurance.insuranceApplicantUnitId =
            checkInsuranceUnitExist(
              defaultData.occupationalInjuryInsurance.insuranceApplicantUnitId,
            );
          defaultData.retirement.insuranceApplicantUnitId =
            checkInsuranceUnitExist(
              defaultData.retirement.insuranceApplicantUnitId,
            );
          defaultData.healthInsurance.insuranceApplicantUnitId =
            checkInsuranceUnitExist(
              defaultData.healthInsurance.insuranceApplicantUnitId,
            );
          defaultData.tax.incomeTaxReturnUnitId = checkIncomeTaxUnitExist(
            defaultData.tax.incomeTaxReturnUnitId,
          );
          defaultData.tax.supplementaryPremiumPartTimeUnitId =
            checkInsuranceUnitExist(
              defaultData.tax.supplementaryPremiumPartTimeUnitId,
            );
          defaultData.payrollSubject.payrollSubjectItems =
            supplementSubjectInfo(
              defaultData.payrollSubject.payrollSubjectItems,
            );
          if (
            defaultData.personalPayroll.salaryCategory ===
            SalaryCategory.monthly
          ) {
            defaultData.payrollSubject.payrollSubjectItems =
              supplementBaseSalarySubject(
                defaultData.payrollSubject.payrollSubjectItems,
              );
          }
          return {
            ...defaultData,
            ...{employee: e},
            ...{
              shouldPresetEffectiveDate: String(
                !defaultData.laborInsurance.effectiveDate &&
                  !defaultData.retirement.effectiveDate &&
                  !defaultData.occupationalInjuryInsurance.effectiveDate &&
                  !defaultData.healthInsurance.effectiveDate &&
                  !defaultData.tax.effectiveDate &&
                  !defaultData.dependentsHealthInsurance.dependentsHealthInsuranceItems.some(
                    (d: any) => !d.addInsuranceDate,
                  ),
              ),
            },
          };
        }
        const initSubjectItems = initMonthlySubject();
        // 單位大於一間時不預設，避免客戶忘記調整
        const initInsuranceApplicantUnitId =
          options.insuranceUnits?.length > 1
            ? undefined
            : options.insuranceUnits?.[0]?.insuranceApplicantUnitId;
        const initIncomeTaxReturnUnitId =
          options.incomeTaxRegistrationUnits?.length > 1
            ? undefined
            : options.incomeTaxRegistrationUnits?.[0]?.incomeTaxReturnUnitId;
        return {
          ...initData,
          ...{employee: e},
          ...{
            laborInsurance: {
              ...initData.laborInsurance,
              insuranceApplicantUnitId: initInsuranceApplicantUnitId,
              insuranceIdentityId: options.insuranceIdentity?.[0]?.value,
            },
            occupationalInjuryInsurance: {
              ...initData.occupationalInjuryInsurance,
              insuranceApplicantUnitId: initInsuranceApplicantUnitId,
              insuranceIdentityId: options.insuranceIdentity?.[0]?.value,
            },
            retirement: {
              ...initData.retirement,
              insuranceApplicantUnitId: initInsuranceApplicantUnitId,
            },
            healthInsurance: {
              ...initData.healthInsurance,
              insuranceApplicantUnitId: initInsuranceApplicantUnitId,
            },
            tax: {
              ...initData.tax,
              incomeTaxReturnUnitId: initIncomeTaxReturnUnitId,
              incomeTaxNumberCode: options.incomeTaxNumbers?.[0]?.value,
              withHoldingTypeCode:
                options.incomeTaxWithholdingTypes?.[0]?.value,
            },
            payrollSubject: {
              payrollSubjectItems: initSubjectItems,
            },
            shouldPresetEffectiveDate: 'true',
          },
        };
      }),
    };
  };

  const supplementSubjectInfo = (source: SalaryAccount[]) => {
    return source.map((subject: any) => {
      const subjectOption = options.salaryAccounts.find(
        (s: any) => s.salaryAccountId === subject.salaryAccountId,
      );
      if (subjectOption) {
        subject.hnSalaryAccountCode = subjectOption.hnSalaryAccountCode;
        subject.salaryAccountCategoryId = subjectOption.salaryAccountCategoryId;
      }
      return subject;
    });
  };
  const supplementBaseSalarySubject = (source: SalaryAccount[]) => {
    const missingSubject = baseSalarySubject.filter(
      b => !source.some(s => s.salaryAccountId === b.salaryAccountId),
    );
    return [
      ...source,
      ...missingSubject.map(m => ({
        hnSalaryAccountCode: null,
        salaryAccountCategoryId: m?.salaryAccountCategoryId,
        salaryAccountId: m?.salaryAccountId,
        money: '0',
      })),
    ];
  };
  const initMonthlySubject = () => {
    const baseSalary = options.salaryAccounts.find(
      (s: SalaryAccount) =>
        s.hnSalaryAccountCode === DefaultBasicSubjectCode.baseSalary,
    );
    const foodAllowance = options.salaryAccounts.find(
      (s: SalaryAccount) =>
        s.hnSalaryAccountCode === DefaultBasicSubjectCode.foodAllowance,
    );
    return [
      {
        hnSalaryAccountCode: baseSalary?.hnSalaryAccountCode,
        salaryAccountCategoryId: baseSalary?.salaryAccountCategoryId,
        salaryAccountId: baseSalary?.salaryAccountId,
        money: '',
      },
      {
        hnSalaryAccountCode: foodAllowance?.hnSalaryAccountCode,
        salaryAccountCategoryId: foodAllowance?.salaryAccountCategoryId,
        salaryAccountId: foodAllowance?.salaryAccountId,
        money: '',
      },
      ...baseSalarySubject.map(s => ({
        hnSalaryAccountCode: null,
        salaryAccountCategoryId: s?.salaryAccountCategoryId,
        salaryAccountId: s?.salaryAccountId,
        money: '0',
      })),
    ];
  };

  const initHourlySubject = () => {
    const hourlySalary = options.salaryAccounts.find(
      (s: SalaryAccount) =>
        s.hnSalaryAccountCode === DefaultBasicSubjectCode.hourlySalary,
    );
    return [
      {
        hnSalaryAccountCode: hourlySalary?.hnSalaryAccountCode,
        salaryAccountCategoryId: hourlySalary?.salaryAccountCategoryId,
        salaryAccountId: hourlySalary?.salaryAccountId,
        money: '',
      },
    ];
  };

  const updateAllErrors = () => {
    const allErrors = data.employeePayrollItems.map((d: any) =>
      validate(d, options),
    );
    setAllErrors(allErrors);
    return allErrors;
  };

  const presetEffectiveDate = (effectiveDate: string) => {
    if (effectiveDate) {
      edit({
        employeePayrollItems: data?.employeePayrollItems.map((d: any) => {
          if (d.employee.id === currentEmployee.employee.id) {
            const formValues = formProps.getValues();
            const updatedFormValues = {
              ...formValues,
              ...{
                laborInsurance: {...formValues.laborInsurance, effectiveDate},
                occupationalInjuryInsurance: {
                  ...formValues.occupationalInjuryInsurance,
                  effectiveDate:
                    isAfterDate(effectiveDate, '2022/05/01', '2022/05/01') ||
                    effectiveDate,
                },
                retirement: {...formValues.retirement, effectiveDate},
                healthInsurance: {...formValues.healthInsurance, effectiveDate},
                tax: {...formValues.tax, effectiveDate},
                dependentsHealthInsurance: {
                  ...formValues.dependentsHealthInsurance,
                  dependentsHealthInsuranceItems:
                    formValues.dependentsHealthInsurance.dependentsHealthInsuranceItems.map(
                      (d: any) => {
                        d.addInsuranceDate = effectiveDate;
                        return d;
                      },
                    ),
                },
                shouldPresetEffectiveDate: 'false',
              },
            };
            formProps.reset(updatedFormValues, {
              errors: true, // errors will not be reset
              dirtyFields: true, // dirtyFields will not be reset
              isDirty: true, // dirty will not be reset
              isSubmitted: true, // isSubmitted will not be reset
            });
            return updatedFormValues;
          }
          return d;
        }),
      });
    }
  };

  useEffect(() => {
    if (!isInit && !isLoading) {
      setIsLoading(true);
    }
    if (!options.isLoading && !isInit && isLoading) {
      let initData: any = {employeePayrollItems: []};
      if (data && data.length !== 0 && status !== StepStatus.init) {
        initData = initEmployee(data?.employeePayrollItems || []);
        if (status === StepStatus.edit) {
          edit(initData);
        } else {
          init(initData);
        }
        setCurrentEmployee(initData.employeePayrollItems?.[0]);
        setIsLoading(false);
        setIsInit(true);
        return;
      }
      api
        .get(`payroll/company/${companyId}/employee/setting`)
        .then(d => {
          if (d.data?.employeePayrollItems) {
            initData = initEmployee(d.data.employeePayrollItems);
            init(initEmployee(d.data.employeePayrollItems));
            setCurrentEmployee(initData.employeePayrollItems?.[0]);
          } else {
            initData = initEmployee();
            init(initData);
            setCurrentEmployee(initData.employeePayrollItems?.[0]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
      setIsInit(true);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, isInit, isLoading]);

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      handleSave();
    }
  }, [saveAndGoStep]);

  useEffect(() => {
    if (allErrors.length === 0 && Object.keys(formProps.errors).length > 0) {
      updateAllErrors();
    }
  }, [formProps.errors]);

  const saveData = () => {
    const _data = {
      employeePayrollItems: data.employeePayrollItems.map((employee: any) => {
        employee.payrollSubject.payrollSubjectItems =
          employee.payrollSubject.payrollSubjectItems.map((subject: any) => {
            const salaryAccount = options.salaryAccounts.find(
              (s: any) => s.salaryAccountId === subject.salaryAccountId,
            );
            if (salaryAccount) {
              subject.salaryAccountCode = salaryAccount.salaryAccountCode;
              subject.salaryAccountName = salaryAccount.salaryAccountName;
            }
            return subject;
          });
        return employee;
      }),
    };
    return api.put(`payroll/company/${companyId}/employee/setting`, {
      data: _data,
    });
  };

  const confirmData = () => {
    return api.put(`payroll/company/${companyId}/employee/setting/confirm`);
  };
  const {mutate: handleSave} = useMutation(saveData, {
    onSuccess: () => {
      save();
    },
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutate: handleConfirm} = useMutation(
    () => saveData()
    .then(() => refetchEnablePY('next', step + 1))
    .then(async (company) => {
      try {
        if (company.enablePY) {
          await confirmData();
          setIsLoading(false)
        }
        changeStep(step + 1);
        confirm();
        remove();
      } catch (e: any) {
        setIsLoading(false)
        throw new Error(e);
      }
    }),
    {
      onMutate: () => setIsLoading(true),
    },
  );

  const getInsuranceAmount = (params: {
    insuranceAmountLevelId?: string;
    insuranceIdentityId: string;
    effectiveDate: string;
    amountLevelType: string;
  }) => {
    return api.get(`payroll/hn/insurance/amount/leave?${qs.stringify(params)}`);
  };

  const updateLocale = React.useCallback(
    debounce(() => {
      const formValues = formProps.getValues();
      edit({
        employeePayrollItems: data.employeePayrollItems.map((d: any) => {
          if (d.employee.id === currentEmployee.employee.id) {
            return formValues;
          }
          return d;
        }),
      });
    }, 500),
    [currentEmployee],
  );

  const removeSubjects = (source: SalaryAccount[], from: SalaryAccount[]) => {
    return source.filter(
      s => !from.some(f => f.salaryAccountId === s.salaryAccountId),
    );
  };
  const modifiySubjects = (source: SalaryAccount[], from: SalaryAccount[]) => {
    let _source = [...source];
    from.forEach(m => {
      const modifiedSubjectIndex = _source.findIndex(
        (s: SalaryAccount) => s.salaryAccountId === m.salaryAccountId,
      );
      if (modifiedSubjectIndex) {
        _source[modifiedSubjectIndex].salaryAccountCategoryId =
          basicSalaryCategoryId;
      } else {
        _source = [
          ..._source,
          {
            ...m,
            money: '0',
          },
        ];
      }
    });
    return _source;
  };
  const addSubjects = (source: SalaryAccount[], from: SalaryAccount[]) => {
    return [
      ...source,
      ...from.map(s => ({
        ...s,
        money: '0',
      })),
    ];
  };
  const updateAllSubjects = (
    originSubjects: SalaryAccount[],
    updatedSubjects: SalaryAccount[],
  ) => {
    const removedSubjects = originSubjects.filter(
      o => !updatedSubjects.some(u => u.salaryAccountId === o.salaryAccountId),
    );
    // 主類別為基本薪資才預設
    const addedSubjects = updatedSubjects.filter(
      u =>
        !originSubjects.some(o => o.salaryAccountId === u.salaryAccountId) &&
        u.salaryAccountCategoryId === basicSalaryCategoryId,
    );
    const modifiedSubjects = updatedSubjects.filter(u =>
      originSubjects.some(
        o =>
          o.salaryAccountId === u.salaryAccountId &&
          o.salaryAccountCategoryId !== u.salaryAccountCategoryId &&
          u.salaryAccountCategoryId === basicSalaryCategoryId,
      ),
    );
    if (
      removedSubjects.length > 0 ||
      addedSubjects.length > 0 ||
      modifiedSubjects.length > 0
    ) {
      edit({
        employeePayrollItems: data.employeePayrollItems.map((d: any) => {
          if (d.employee.id === currentEmployee.employee.id) {
            const formValues = formProps.getValues();
            if (
              formValues.personalPayroll.salaryCategory ===
              SalaryCategory.monthly
            ) {
              if (removedSubjects.length > 0) {
                formValues.payrollSubject.payrollSubjectItems = removeSubjects(
                  formValues.payrollSubject.payrollSubjectItems,
                  removedSubjects,
                );
              }
              if (modifiedSubjects.length > 0) {
                formValues.payrollSubject.payrollSubjectItems = modifiySubjects(
                  formValues.payrollSubject.payrollSubjectItems,
                  modifiedSubjects,
                );
              }
              if (addedSubjects.length > 0) {
                formValues.payrollSubject.payrollSubjectItems = addSubjects(
                  formValues.payrollSubject.payrollSubjectItems,
                  addedSubjects,
                );
              }
              formProps.setValue(
                subjectDataName,
                formValues.payrollSubject.payrollSubjectItems,
              );
            }
            return formValues;
          }
          if (d.personalPayroll.salaryCategory === SalaryCategory.monthly) {
            if (removedSubjects.length > 0) {
              d.payrollSubject.payrollSubjectItems = removeSubjects(
                d.payrollSubject.payrollSubjectItems,
                removedSubjects,
              );
            }
            if (modifiedSubjects.length > 0) {
              d.payrollSubject.payrollSubjectItems = modifiySubjects(
                d.payrollSubject.payrollSubjectItems,
                modifiedSubjects,
              );
            }
            if (addedSubjects.length > 0) {
              d.payrollSubject.payrollSubjectItems = addSubjects(
                d.payrollSubject.payrollSubjectItems,
                addedSubjects,
              );
            }
          }
          return d;
        }),
      });
    }
  };

  const filterDefaultSubject = (subjects: SalaryAccount[]) =>
    subjects.filter(subject => !subject.hnSalaryAccountCode);
  const handleSubjectFormSubmit = async (submitData: {
    salarySubjects: SalaryAccount[];
  }) => {
    setIsLoading(true);
    try {
      const originSubjects = filterDefaultSubject(
        cloneDeep(options.salaryAccounts),
      );
      await saveSalaryAccount(
        submitData.salarySubjects.map(s => {
          s.hnSalaryAccountCode = s.hnSalaryAccountCode || null;
          s.hnSalaryAccountName = s.hnSalaryAccountName || null;
          s.salaryAccountId = s.salaryAccountId || null;
          return s;
        }),
      );
      const response: any = await options.refetchSalaryAccounts();
      const updatedSubjects = filterDefaultSubject(response.data);
      updateAllSubjects(originSubjects, updatedSubjects);
      setShowSalarySubjectForm(false);
      setIsLoading(false);
    } catch (ex) {
      console.error(ex);
      setIsLoading(false);
    }
  };

  const handleNext = formProps.handleSubmit(
    () => {
      const allErrors = updateAllErrors();

      if (allErrors.every((e: any) => e === null)) {
        handleConfirm();
      } else {
        setShowFailDialog(true);
      }
    },
    () => {
      setShowFailDialog(true);
    },
  );

  const downloadExcelSample = async () => {
    const downloadUrl = await api.get('payroll/sample/excel');
    if (downloadUrl) {
      const response = await fetch(downloadUrl, {
        method: 'get',
        cache: 'no-cache',
      });
      const blob = await response.blob();
      exportExcelData({blob, name: `員工薪資主檔範本.xlsx`});
    }
  };

  const uploadExcelSample = async (event: React.ChangeEvent<any>) => {
    mutate({file: event?.currentTarget?.files[0]});
    event.target.value = null;
  };

  return (
    <React.Fragment>
      <Layout.MainContent>
        <PageToolBar pageTitle={config.payroll.steps.employee.name}>
          <IconButton
            onClick={() => setShowResetDialog(true)}
            className={'reset-btn'}
          >
            <FontAwesomeIcon icon={['fal', 'redo']} />
            全部重設
          </IconButton>
          <ToolDivider />
          <IconButton
            onClick={downloadExcelSample}
            className="gap-[3px]"
            aria-label="download-excel-sample"
          >
            <DownloadIcon />
            Excel範本下載
          </IconButton>
          <ToolDivider />
          <label
            className={cx(
              'text-[13px] bg-transparent px-[6px] py-[1px] m-0 leading-none border-0 outline-none',
              'flex items-center justify-center gap-[3px]',
              'inherit hover:text-brand cursor-pointer',
            )}
            aria-label="upload-excel-sample"
          >
            <input
              id="upload_img"
              className="hidden"
              type="file"
              accept=".xlsx"
              onChange={uploadExcelSample}
            />
            <UploadIcon />
            批次上傳
          </label>
        </PageToolBar>
        <div
          id="employee-setting"
          className={'flex min-w-[calc(100vw-60px)] min-h-[calc(100vh-395px)]'}
        >
          <EmployeeSidebar
            edit={edit}
            disabled={options.isLoading}
            formProps={formProps}
            employees={data?.employeePayrollItems || []}
            allErrors={allErrors}
            updateAllErrors={updateAllErrors}
            currentEmployee={currentEmployee}
            setCurrentEmployee={setCurrentEmployee}
          />
          <FormContent
            updateLocale={updateLocale}
            formProps={formProps}
            options={options}
            currentEmployee={currentEmployee}
            getInsuranceAmount={getInsuranceAmount}
            openSalarySubjectForm={() => setShowSalarySubjectForm(true)}
            presetEffectiveDate={presetEffectiveDate}
            initMonthlySubject={initMonthlySubject}
            initHourlySubject={initHourlySubject}
          />
          <SalarySubjectForm
            isOpen={showSalarySubjectForm}
            onClose={() => setShowSalarySubjectForm(false)}
            onConfirm={handleSubjectFormSubmit}
            defaultValues={options.salaryAccounts}
            options={options}
            getEmployeeData={() => {
              return (data?.employeePayrollItems || []).map((d: any) => {
                if (d.employee.id === currentEmployee?.employee?.id) {
                  return formProps.getValues();
                }
                return d;
              });
            }}
          />
        </div>
      </Layout.MainContent>
      <Layout.Footer
        updatedTime={updatedTime}
        showNextButton
        showPrevButton
        showSaveButton
        showUpdatedTime
        disabledSaveButton={
          status !== StepStatus.edit || isLoading || options.isLoading
        }
        disabledPrevButton={
          status === StepStatus.edit || isLoading || options.isLoading
        }
        disabledNextButton={isLoading || options.isLoading}
        onSave={handleSave}
        onNext={handleNext}
        onPrev={async () => {
          await onPrev();
          remove();
        }}
      />
      <ResetDialog
        isOpen={showResetDialog}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={() => {
          const initData = initEmployee();
          init(initData);
          setCurrentEmployee(initData.employeePayrollItems?.[0]);
          setShowResetDialog(false);
          setShowResetSuccessDialog(true);
        }}
      />
      <FailDialog
        text={errorMessage}
        isOpen={showUploadFailDialog}
        onConfirm={() => setShowUploadFailDialog(false)}
        onCancel={() => setShowUploadFailDialog(false)}
      />
      <FailDialog
        isOpen={showFailDialog}
        onConfirm={() => setShowFailDialog(false)}
        onCancel={() => setShowFailDialog(false)}
      />
      <ResetSuccessDialog
        isOpen={showResetSuccessDialog}
        onCancel={() => setShowResetSuccessDialog(false)}
      />
      {!showSalarySubjectForm && <DevTool control={formProps.control} />}
    </React.Fragment>
  );
}
